/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { PROGRESS_BAR_TEST_ID_SUFFIX } from './constants';
import ProgressBar from './progress-bar';
import type { SimpleBarVariantProps } from './types';
import { useFakeProgress } from './use-fake-progress';

const messages: {
	loadingLabel: {
		defaultMessage: string;
		description: string;
		id: string;
	};
} = defineMessages({
	loadingLabel: {
		id: 'native-embeds-core.common.loading-overlay.simple-bar-variant.loadingLabel',
		defaultMessage: 'Loading',
		description: 'Label for the simple loading bar shown while a native embed is loading.',
	},
});

const containerStyles = css({
	position: 'absolute',
	inset: 0,
	display: 'flex',
	alignItems: 'flex-end',
	justifyContent: 'center',
	paddingBlockEnd: token('space.150', '12px'),
	backgroundColor: token('elevation.surface.sunken'),
	zIndex: 1,
});

const blanketStyles = css({
	position: 'absolute',
	inset: 0,
	// Matches AIGeneratingOverlay's blanket so the handoff from the Rovo
	// thinking overlay to this loading bar is visually seamless.
	backgroundColor: token('color.blanket', 'rgba(9, 30, 66, 0.36)'),
});

const progressBarWrapperStyles = css({
	width: '95%',
	position: 'relative',
	zIndex: 1,
});

export default function SimpleBarVariant({
	animationDisabled = false,
	showBlanket = false,
	testId,
}: SimpleBarVariantProps): JSX.Element {
	const { formatMessage } = useIntl();
	const progress = useFakeProgress(animationDisabled);

	return (
		<div css={containerStyles} data-testid={testId}>
			{showBlanket && <div css={blanketStyles} />}
			<div css={progressBarWrapperStyles}>
				<ProgressBar
					ariaLabel={formatMessage(messages.loadingLabel)}
					testId={testId ? `${testId}--${PROGRESS_BAR_TEST_ID_SUFFIX}` : undefined}
					value={progress}
				/>
			</div>
		</div>
	);
}
